/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useEffect, useMemo } from "react";

import { css, jsx } from "@compiled/react";

import { AtlassianLogo } from "@atlaskit/logo";
import { N800 } from "@atlaskit/theme/colors";
import { token } from "@atlaskit/tokens";

export interface ActiveEntitlementsProps {
    readonly imageAssets: {
        readonly urlInBrowser: string;
    };
    readonly site: string;
    readonly activeEntitlementsRedirectTimer: number; // In ms
    readonly activeEntitlementsRedirect: () => void;
}

const containerStyles = css({
    width: 668,
    maxWidth: "100%",
    overflow: "hidden",
    margin: `${token("space.1000", "72px")} auto 0`,
    fontFamily: token("font.family.brand.body")
});

const centerStyles = css({
    textAlign: "center",
    padding: `0 ${token("space.300", "27.5px")}`
});

const titleStyles = css({
    fontSize: 36,
    lineHeight: "44px",
    marginBlockStart: token("space.400", "32px"),
    marginBlockEnd: token("space.400", "32px"),
    fontFamily: token("font.family.brand.heading"),
    fontWeight: token("font.weight.semibold")
});

const messageStyles = css({
    fontSize: 16,
    lineHeight: "24px",
    marginBlockEnd: token("space.800", "64px")
});

const imageStyles = css({
    backgroundRepeat: "no-repeat",
    backgroundPosition: "left top",
    width: 668,
    maxWidth: "100%",
    height: 474,
    position: "relative"
});

const urlStyles = css({
    "background": token("color.background.input", "#F5F5F7"),
    "color": token("color.text", N800),
    "fontSize": "12px",
    "lineHeight": "20px",
    "height": "20px",
    "width": 360,
    // TODO: Use new breakpoints, info at https://staging.atlassian.design/components/primitives/responsive/breakpoints/examples

    "@media (max-width: 540px)": {
        width: "calc(100% - 180px)"
    },
    "overflow": "hidden",
    "position": "absolute",
    "insetInlineStart": "177px",

    "insetBlockStart": token("space.300", "24px"),
    "whiteSpace": "nowrap",
    "zIndex": 1
});

export const ActiveEntitlements = ({
    imageAssets,
    site,
    activeEntitlementsRedirectTimer,
    activeEntitlementsRedirect
}: ActiveEntitlementsProps) => {
    useEffect(() => {
        setTimeout(activeEntitlementsRedirect, activeEntitlementsRedirectTimer);
    }, [activeEntitlementsRedirect, activeEntitlementsRedirectTimer]);

    const siteHostname = useMemo(() => {
        try {
            return new URL(site).hostname;
        } catch (e) {
            return site;
        }
    }, [site]);

    return (
        <div css={containerStyles}>
            <div css={centerStyles}>
                <AtlassianLogo appearance="brand" size="medium" />
            </div>
            <h2 css={[titleStyles, centerStyles]}>Your site is ready</h2>
            <div css={[messageStyles, centerStyles]}>
                We're creating your site <strong>{site}</strong>. This should
                only take about a minute. We'll take you there now.
            </div>
            <div
                css={imageStyles}
                style={{
                    backgroundImage: `url("${imageAssets.urlInBrowser}")`
                }}
            >
                <div css={urlStyles}>{siteHostname}</div>
            </div>
        </div>
    );
};
